import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import EmptyState from './common/EmptyState.jsx';
import { linkDerivation, searchFilesByName, setFileStage } from '../services/DerivationService.mjs';
import { notify, reportError } from '../notifications/errorBus';
import '../css/BulkActions.css';

const SKIP = { skipGlobalErrorToast: true };
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Link every selected file to one source, or classify them all at once.
 *
 * The case this exists for is real and common: one `.blend` produces three trim
 * variants, or a dozen. Doing that one file at a time through the details modal
 * is twelve dialogs to state one fact. Here it is one.
 *
 * Unlike the project modal, this DOES loop per file — there is no batch link
 * endpoint, because each link is a two-document transaction with its own cycle
 * check. So the result is reported as a tally, and a file that fails does not
 * abandon the rest.
 */
const BulkDerivationModal = ({ files, onClose, onApplied }) => {
    const [tab, setTab] = useState('link');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const searchSeqRef = useRef(0);

    const fileIds = useMemo(() => files.map((f) => f.id), [files]);
    const selectedIds = useMemo(() => new Set(fileIds), [fileIds]);

    useEffect(() => {
        if (tab !== 'link') return undefined;
        const seq = ++searchSeqRef.current;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const hits = await searchFilesByName(query, { hitsPerPage: 12 }, SKIP);
                if (seq !== searchSeqRef.current) return;
                // A file cannot be its own source, and offering one of the
                // selected files would silently drop it from the operation.
                setResults(hits.filter((h) => !selectedIds.has(h.id)).slice(0, 8));
            } catch {
                if (seq === searchSeqRef.current) setResults([]);
            } finally {
                if (seq === searchSeqRef.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, tab, selectedIds]);

    /**
     * Report what actually happened rather than "done".
     *
     * A bulk action over a stale selection partially fails as a matter of
     * course — someone else deleted a file, or one of these is already the
     * source's own ancestor and the cycle check refused it.
     */
    const finish = (linked, failed, verb) => {
        if (linked > 0) {
            notify({
                type: 'success',
                title: `Files ${verb}`,
                message: `${linked} file${linked === 1 ? '' : 's'} ${verb}.`,
            });
        }
        if (failed > 0) {
            reportError(
                `${failed} file${failed === 1 ? '' : 's'} could not be updated — they may have been deleted, or linking them would have made a loop.`,
                { title: 'Some files were skipped' },
            );
        }
        onApplied?.();
        onClose?.();
    };

    const applyLink = async (source) => {
        if (busy) return;
        setBusy(true);
        let linked = 0;
        let failed = 0;
        for (const fileId of fileIds) {
            try {
                await linkDerivation(source.id, fileId, {}, SKIP);
                linked++;
            } catch {
                failed++;
            }
        }
        setBusy(false);
        finish(linked, failed, `linked to ${source.name}`);
    };

    const applyStage = async (stage) => {
        if (busy) return;
        setBusy(true);
        let done = 0;
        let failed = 0;
        for (const fileId of fileIds) {
            try {
                await setFileStage(fileId, stage, SKIP);
                done++;
            } catch {
                failed++;
            }
        }
        setBusy(false);
        finish(done, failed, stage ? `marked as ${stage}` : 'cleared');
    };

    return (
        <Modal onClose={onClose}>
            <div className="bulk-modal">
                <div className="bulk-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Source &amp; exports</h2>
                        <p>{files.length} file(s) selected</p>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                <div className="bulk-tabs">
                    <button className={`bulk-tab ${tab === 'link' ? 'active' : ''}`} onClick={() => setTab('link')}>
                        Link to a source
                    </button>
                    <button className={`bulk-tab ${tab === 'stage' ? 'active' : ''}`} onClick={() => setTab('stage')}>
                        Set stage
                    </button>
                </div>

                {tab === 'link' && (
                    <div className="bulk-section">
                        <p className="bulk-field-hint">
                            Every selected file will be recorded as made from the file you pick.
                        </p>
                        <input
                            className="bulk-create-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Find the source file…"
                            aria-label="Find the source file"
                            disabled={busy}
                            autoFocus
                        />
                        {searching ? (
                            <p className="bulk-field-hint">Searching…</p>
                        ) : results.length === 0 ? (
                            <EmptyState
                                compact
                                title={query ? 'No matching files' : 'Search for a source'}
                                description={query ? 'Try a different name.' : 'Type part of the file name.'}
                            />
                        ) : (
                            <div className="bulk-pill-list">
                                {results.map((hit) => (
                                    <button
                                        key={hit.id}
                                        className="bulk-pill-btn"
                                        onClick={() => applyLink(hit)}
                                        disabled={busy}
                                        title={`Record ${files.length} file(s) as made from ${hit.name}`}
                                    >
                                        {hit.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'stage' && (
                    <div className="bulk-section">
                        <p className="bulk-field-hint">
                            A folder bound to one stage only syncs the files that belong to it.
                        </p>
                        <div className="bulk-pill-list">
                            {[
                                ['source', 'Source'],
                                ['export', 'Export'],
                                ['raw', 'Raw'],
                                [null, 'Clear'],
                            ].map(([value, label]) => (
                                <button
                                    key={label}
                                    className="bulk-pill-btn"
                                    onClick={() => applyStage(value)}
                                    disabled={busy}
                                    title={`Mark ${files.length} file(s) as ${label.toLowerCase()}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BulkDerivationModal;
